// Lightweight self-hosted error reporting → /.netlify/functions/log-error → Supabase `client_errors`.
// Privacy: sends only the error text, stack, page path (no query string / no user data).
// Safe by design: never throws, never shows anything to the user, capped per session.

const ENDPOINT = (process.env.REACT_APP_API_BASE || "/.netlify/functions") + "/log-error";
const MAX_PER_SESSION = 8;
const seen = new Set();
let sent = 0;

// Anonymous per-tab id (random, not tied to a person) so the admin can count how many users an error hit.
let SESSION = "";
try {
  SESSION = sessionStorage.getItem("roam-err-sid") || Math.random().toString(36).slice(2, 12);
  sessionStorage.setItem("roam-err-sid", SESSION);
} catch (e) { SESSION = Math.random().toString(36).slice(2, 12); }

// Last few things the user did (page changes + taps on buttons/links). Never captures typed text.
const crumbs = [];
function addCrumb(t, m) {
  try {
    crumbs.push({ t, m: String(m || "").replace(/\s+/g, " ").trim().slice(0, 60), ts: Date.now() });
    if (crumbs.length > 8) crumbs.shift();
  } catch (e) {}
}

// Known harmless browser noise — not worth logging.
const IGNORE = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Non-Error promise rejection captured/i,
  /AbortError/i,
  /The operation was aborted/i,
  /Load failed$/i,            // iOS Safari: request cancelled / offline
  /Failed to fetch$/i,        // offline / slow network, not an app bug
  /NetworkError when attempting to fetch/i,
];

export function reportError(err, source = "window") {
  try {
    const message = String((err && (err.message || err.reason || err)) || "Unknown error");
    if (IGNORE.some((re) => re.test(message))) return;
    const stack = (err && err.stack) ? String(err.stack) : "";
    const key = message + "|" + stack.split("\n")[1];
    if (seen.has(key) || sent >= MAX_PER_SESSION) return;
    seen.add(key); sent++;

    const payload = JSON.stringify({
      message, stack, source,
      page: window.location.pathname,
      session_id: SESSION,
      breadcrumbs: JSON.stringify(crumbs),
    });
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      if (ok) return;
    }
    fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
  } catch (e) { /* reporting must never break the app */ }
}

export function initErrorReporter() {
  try {
    addCrumb("navigation", window.location.pathname);
    const wrap = (name) => {
      const orig = history[name];
      if (typeof orig !== "function") return;
      history[name] = function () {
        const r = orig.apply(this, arguments);
        addCrumb("navigation", window.location.pathname);
        return r;
      };
    };
    wrap("pushState"); wrap("replaceState");
    window.addEventListener("popstate", () => addCrumb("navigation", window.location.pathname));
    document.addEventListener("click", (e) => {
      const el = e.target && e.target.closest ? e.target.closest("button,a,[role=button]") : null;
      if (!el) return;
      const label = el.getAttribute("aria-label") || el.innerText || el.id || el.tagName.toLowerCase();
      addCrumb("click", el.tagName.toLowerCase() + " " + label);
    }, true);
  } catch (e) {}
  window.addEventListener("error", (e) => reportError(e.error || e.message, "window.onerror"));
  window.addEventListener("unhandledrejection", (e) => reportError(e.reason, "unhandledrejection"));
}
